import TraceService from "@/services/api/TraceService";
import useGlobalStore from "@/services/store/useGlobalStore";
import { addColorToUsers, mapUsersByGroupId } from "@/utils/functions/user";
import { angle } from "@/utils/trace/trace-utils";
import { useMemo } from "react";
import useUsers from "./useUsers";

const useTraces = () => {
  const { traces, setTraces, setUsers, setIsDeletingTraces, addSelectedTrace, selectTrace, deselectTrace, deselectTraces } = useGlobalStore(
    (state) => state
  );
  const { getUsers, usersByLabel } = useUsers();

  const tracesById: Dic<Trace> = useMemo(
    () => traces.reduce((curr: Dic<Trace>, next: Trace) => ({ ...curr, [next.id]: next }), {}),
    [traces]
  );

  const getTraces = () => {
    return new Promise((resolve) => {
      TraceService.getTraces().then(setTraces).then(resolve);
    });
  };

  const fetchUsersAndTraces = () => {
    return new Promise((resolve) => {
      getTraces()
        .then(() => getUsers())
        .then((usersResponse) => {
          const usersByGroup = mapUsersByGroupId(usersResponse as User[]);
          const usersWithColor = addColorToUsers(usersResponse as User[], usersByGroup);

          setUsers(usersWithColor);
        })
        .then(resolve);
    });
  };

  const createTrace = (trace: Trace, userId?: number) => {
    return new Promise((resolve) => {
      TraceService.createTrace(trace, userId ?? null)
        .then(fetchUsersAndTraces)
        .then(resolve);
    });
  };

  const addPointToTrace = (trace: Trace, point: Point) => {
    return new Promise((resolve) => {
      TraceService.addPointToTrace(trace, point)
        .then(() => fetchUsersAndTraces())
        .then(resolve);
    });
  };

  const removePointFromTrace = (point: Point) => {
    return new Promise((resolve) => {
      TraceService.removePointFromTrace(point)
        .then(() => fetchUsersAndTraces())
        .then(resolve);
    });
  };

  const updateTrace = (trace: Trace, userId: number) => {
    return new Promise((resolve) => {
      TraceService.updateTrace(trace, userId)
        .then(() => fetchUsersAndTraces())
        .then(resolve);
    });
  };

  const deleteTrace = (trace: Trace) => {
    return new Promise((resolve) => {
      TraceService.deleteTrace(trace)
        .then(() => fetchUsersAndTraces())
        .then(resolve);
    });
  };

  const deleteAllTraces = (traces: Trace[]) => {
    setIsDeletingTraces(true);
    return new Promise((resolve) => {
      Promise.allSettled(traces.map(TraceService.deleteTrace))
        .then(() => fetchUsersAndTraces())
        .then(resolve)
        .finally(() => {
          setIsDeletingTraces(false);
        });
    });
  };

  const reverseTrace = (trace: Trace) => {
    const currentPoints = trace.points;
    const nbPoints = currentPoints.length;
    const newTracePoints = currentPoints.toReversed().map((point, index) => {
      const { timestamp } = trace.points[nbPoints - 1 - index];
      return { ...point, index, timestamp };
    });
    newTracePoints.forEach((_, index) => {
      if (index > 0) newTracePoints[index].r = angle(newTracePoints[index - 1], newTracePoints[index]);
    });
    const lastPoint = newTracePoints[newTracePoints.length - 1];
    newTracePoints[newTracePoints.length - 1] = {
      ...lastPoint,
      r: angle(newTracePoints[newTracePoints.length - 2], lastPoint),
    };
    const newTrace = { ...trace, points: newTracePoints };

    return new Promise((resolve) => {
      Promise.all([TraceService.deleteTrace(trace), TraceService.createTrace(newTrace, newTrace.userId)])
        .then(() => fetchUsersAndTraces())
        .then(resolve);
    });
  };

  const addTraceToUser = (trace: Trace, userLabel: string) => {
    if (usersByLabel[userLabel]) {
      trace.userId = usersByLabel[userLabel].id;
      createTrace(trace, usersByLabel[userLabel].id);
    }
  };

  const updateTraceFromWs = (traceUpdated: TraceUpdatedWS) => {
    // The backend sends two different payloads for TRACE_UPDATED:
    // 1. A full point object {id, x, y, z, r, timestamp, traceId, buttonStatus} — real-time GPS point
    // 2. A plain trace ID number — mission stop/arrived signal (trace was closed)
    // In case 2, JSON.parse returns a number so traceUpdated.traceId is undefined.
    if (traceUpdated.traceId == null) {
      if (!useGlobalStore.getState().isDeletingTraces) getTraces();
      return;
    }

    if (traces.some((trace) => trace.id === traceUpdated.traceId)) {
      setTraces(
        traces.map((trace) => {
          if (trace.id === traceUpdated.traceId) {
            return {
              ...trace,
              points: [
                ...trace.points,
                {
                  id: traceUpdated.id,
                  x: traceUpdated.x,
                  y: traceUpdated.y,
                  z: traceUpdated.z,
                  r: traceUpdated.r,
                  timestamp: traceUpdated.timestamp,
                  buttonStatus: traceUpdated.buttonStatus,
                },
              ],
            };
          }
          return trace;
        })
      );
    } else if (!useGlobalStore.getState().isDeletingTraces) getTraces();
  };

  return {
    updateTraceFromWs,
    addTraceToUser,
    reverseTrace,
    deleteAllTraces,
    deleteTrace,
    updateTrace,
    removePointFromTrace,
    fetchUsersAndTraces,
    getTraces,
    createTrace,
    addPointToTrace,
    tracesById,
    traces,
    addSelectedTrace,
    selectTrace,
    deselectTrace,
    deselectTraces,
  };
};

export default useTraces;
